import { Link, useNavigate } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePremium } from '../hooks/usePremium';
import { useProfile, getRarityBg, formatResponseTime } from '../hooks/useProfile';
import { motion } from 'framer-motion';
import SpotifyLinkingModal from '../components/SpotifyLinkingModal';
import { 
  User, 
  Trophy, 
  Music, 
  Target, 
  Zap,
  Award,
  Clock,
  Flame,
  Star,
  TrendingUp,
  Calendar,
  Crown,
  Loader2,
  RefreshCw,
  Link as LinkIcon,
  Unlink,
  LogOut
} from 'lucide-react';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { player, user, isAuthenticated, logout, token } = useAuthStore();
  const { isPremium, daysRemaining } = usePremium();
  const { profile, allBadges, isLoading, fetchProfile } = useProfile();
  const [showSpotifyModal, setShowSpotifyModal] = useState(false);
  
  const handleLinkSpotify = () => {
    // Afficher la modale de chargement
    setShowSpotifyModal(true);
  };
  
  const handleSpotifyRedirect = useCallback(() => {
    // Rediriger vers l'auth Spotify avec le token JWT
    window.location.href = `/auth/spotify/link?token=${token}`;
  }, [token]);
  
  const handleUnlinkSpotify = async () => {
    try {
      const response = await fetch('/auth/spotify/unlink', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        // Rafraîchir le profil
        fetchProfile();
      }
    } catch (error) {
      console.error('Erreur unlink Spotify:', error);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  if (!isAuthenticated || !player) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <User className="w-16 h-16 text-dark-500 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold mb-2">Non connecté</h1>
        <p className="text-dark-400 mb-6">
          Connectez-vous pour voir votre profil et vos statistiques.
        </p>
        <Link to="/" className="btn-primary inline-flex items-center gap-2">
          Retour à l'accueil
        </Link>
      </div>
    );
  }
  
  // Afficher un loader si on charge le profil
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <Loader2 className="w-12 h-12 text-primary-500 mx-auto mb-4 animate-spin" />
        <p className="text-dark-400">Chargement du profil...</p>
      </div>
    );
  }
  
  // Stats depuis le profil ou valeurs par défaut
  const stats = profile ? {
    gamesPlayed: profile.gamesPlayed,
    gamesWon: profile.gamesWon,
    winRate: profile.winRate,
    totalScore: profile.totalScore,
    titlesFound: profile.titlesFound,
    artistsFound: profile.artistsFound,
    perfectGuesses: profile.perfectGuesses,
    bestStreak: profile.bestStreak,
    currentStreak: profile.currentStreak,
    avgResponseTime: profile.avgResponseTime,
    rankScore: profile.rankScore,
    rankWins: profile.rankWins,
    level: profile.level,
    xp: profile.xp,
  } : {
    gamesPlayed: 0,
    gamesWon: 0,
    winRate: 0,
    totalScore: 0,
    titlesFound: 0,
    artistsFound: 0,
    perfectGuesses: 0,
    bestStreak: 0,
    currentStreak: 0,
    avgResponseTime: 0,
    rankScore: 0,
    rankWins: 0,
    level: player.level || 1,
    xp: player.xp || 0,
  };
  
  // Calculer l'XP pour le niveau suivant
  const xpForCurrentLevel = Math.pow((stats.level - 1), 2) * 100;
  const xpForNextLevel = Math.pow(stats.level, 2) * 100;
  const xpProgress = stats.xp - xpForCurrentLevel;
  const xpNeeded = xpForNextLevel - xpForCurrentLevel;
  const xpPercent = Math.round((xpProgress / xpNeeded) * 100);
  
  // Badges du profil + non obtenus
  const earnedBadgeIds = new Set(profile?.badges.map(b => b.id) || []);
  const unearnedBadges = allBadges.filter(b => !earnedBadgeIds.has(b.id));
  
  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header profil */}
        <div className="card mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {player.avatar ? (
              <img 
                src={player.avatar} 
                alt={player.name} 
                className="w-24 h-24 rounded-full border-4 border-primary-500"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-4xl font-bold">
                {player.name.charAt(0).toUpperCase()}
              </div>
            )}
            
            <div className="text-center md:text-left flex-1">
              <div className="flex items-center gap-3 justify-center md:justify-start mb-1">
                <h1 className="font-display text-3xl font-bold">{player.name}</h1>
                {isPremium && (
                  <span 
                    className="px-2 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-full text-yellow-400 text-xs font-semibold flex items-center gap-1"
                    title={daysRemaining ? `${daysRemaining} jours restants` : 'Premium actif'}
                  >
                    <Crown className="w-3 h-3" />
                    PREMIUM
                  </span>
                )}
                {stats.rankScore <= 3 && stats.rankScore > 0 && (
                  <span className="text-yellow-400" title={`Rang #${stats.rankScore} mondial`}>
                    <Crown className="w-6 h-6" />
                  </span>
                )}
              </div>
              <p className="text-dark-400 mb-3">
                {user?.spotify_id ? '🎵 Spotify lié' : '👤 Compte BlindTest'}
                {stats.currentStreak > 0 && (
                  <span className="ml-2 text-orange-400">
                    🔥 {stats.currentStreak} victoires d'affilée
                  </span>
                )}
              </p>
              
              {/* Promo Premium si pas premium */}
              {!isPremium && (
                <Link
                  to="/premium"
                  className="mb-3 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-xl text-yellow-300 text-sm hover:border-yellow-400 transition-all"
                >
                  <Crown className="w-4 h-4" />
                  Passer Premium à 5€/mois
                </Link>
              )}
              
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                {/* Niveau */}
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <p className="text-dark-400 text-xs">Niveau</p>
                    <p className="font-bold text-lg">{stats.level}</p>
                  </div>
                </div>
                
                {/* XP Progress */}
                <div className="flex items-center gap-2 text-sm flex-1 min-w-[150px]">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-dark-400 mb-1">
                      <span>XP</span>
                      <span>{xpProgress} / {xpNeeded}</span>
                    </div>
                    <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-primary-500 to-secondary-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${xpPercent}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Classement */}
                {stats.rankScore > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-dark-400 text-xs">Classement</p>
                      <p className="font-bold text-lg">#{stats.rankScore}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Bouton refresh */}
            <button 
              onClick={fetchProfile}
              className="p-2 text-dark-400 hover:text-white transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Statistiques principales */}
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary-400" />
          Statistiques
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            label="Parties jouées" 
            value={stats.gamesPlayed} 
            icon={<Music className="w-5 h-5" />}
          />
          <StatCard 
            label="Victoires" 
            value={stats.gamesWon}
            icon={<Trophy className="w-5 h-5" />}
            subValue={`${stats.winRate}%`}
          />
          <StatCard 
            label="Score total" 
            value={stats.totalScore.toLocaleString()}
            icon={<Star className="w-5 h-5" />}
          />
          <StatCard 
            label="Meilleur combo" 
            value={stats.bestStreak}
            icon={<Flame className="w-5 h-5" />}
          />
        </div>
        
        {/* Stats détaillées */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            label="Titres trouvés" 
            value={stats.titlesFound}
            icon={<Music className="w-5 h-5" />}
            small
          />
          <StatCard 
            label="Artistes trouvés" 
            value={stats.artistsFound}
            icon={<User className="w-5 h-5" />}
            small
          />
          <StatCard 
            label="Réponses parfaites" 
            value={stats.perfectGuesses}
            icon={<Target className="w-5 h-5" />}
            small
          />
          <StatCard 
            label="Temps moyen" 
            value={formatResponseTime(stats.avgResponseTime)}
            icon={<Clock className="w-5 h-5" />}
            small
          />
        </div>
        
        {/* Badges obtenus */}
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary-400" />
          Badges obtenus ({profile?.badges.length || 0})
        </h2>
        
        {profile?.badges && profile.badges.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {profile.badges.map((badge) => (
              <motion.div 
                key={badge.id}
                className={`card border ${getRarityBg(badge.rarity)}`}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{badge.icon}</span>
                  <div>
                    <p className="font-semibold">{badge.name}</p>
                    <p className="text-dark-400 text-sm">{badge.description}</p>
                    <p className="text-dark-500 text-xs mt-1">
                      Obtenu le {new Date(badge.earnedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-8 mb-8 border-dashed border-dark-600">
            <Award className="w-12 h-12 text-dark-500 mx-auto mb-2" />
            <p className="text-dark-400">Aucun badge obtenu pour l'instant</p>
            <p className="text-dark-500 text-sm">Jouez des parties pour en débloquer !</p>
          </div>
        )}
        
        {/* Badges à débloquer */}
        {unearnedBadges.length > 0 && (
          <>
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-dark-500" />
              Badges à débloquer ({unearnedBadges.length})
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {unearnedBadges.map((badge) => (
                <div 
                  key={badge.id}
                  className="card opacity-50 grayscale"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{badge.icon}</span>
                    <div>
                      <p className="font-semibold">{badge.name}</p>
                      <p className="text-dark-400 text-sm">{badge.description}</p>
                      <p className="text-dark-500 text-xs mt-1">
                        +{badge.xpReward} XP
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Historique des parties */}
        {profile?.recentGames && profile.recentGames.length > 0 && (
          <>
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-400" />
              Parties récentes
            </h2>
            
            <div className="space-y-3 mb-8">
              {profile.recentGames.map((game) => (
                <motion.div 
                  key={game.id}
                  className="card flex items-center gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {/* Rank */}
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl
                    ${game.rank === 1 ? 'bg-yellow-500/20 text-yellow-400' : 
                      game.rank === 2 ? 'bg-gray-400/20 text-gray-300' :
                      game.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-dark-600 text-dark-400'}`}
                  >
                    #{game.rank}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1">
                    <p className="font-semibold">{game.roomName}</p>
                    <p className="text-dark-400 text-sm">
                      {game.playerCount} joueurs • {game.titlesFound} titres trouvés
                      {game.perfectGuesses > 0 && ` • ${game.perfectGuesses} parfaits`}
                    </p>
                  </div>
                  
                  {/* Score */}
                  <div className="text-right">
                    <p className="font-bold text-lg">{game.finalScore.toLocaleString()}</p>
                    <p className="text-dark-500 text-xs">
                      {new Date(game.finishedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
        
        {/* Section Paramètres du compte */}
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-primary-400" />
          Paramètres du compte
        </h2>
        
        <div className="card mb-8 space-y-4">
          {/* Lien Spotify */}
          <div className="flex items-center justify-between py-3 border-b border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1DB954]/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1DB954]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium">Compte Spotify</p>
                <p className="text-dark-400 text-sm">
                  {user?.spotify_id 
                    ? 'Lié - Accédez à vos playlists Spotify' 
                    : 'Non lié - Liez votre compte pour utiliser vos playlists'}
                </p>
              </div>
            </div>
            {user?.spotify_id ? (
              <button
                onClick={handleUnlinkSpotify}
                className="btn-ghost text-red-400 hover:text-red-300 flex items-center gap-2"
              >
                <Unlink className="w-4 h-4" />
                Délier
              </button>
            ) : (
              <button
                onClick={handleLinkSpotify}
                className="btn-secondary flex items-center gap-2 bg-[#1DB954]/20 hover:bg-[#1DB954]/30 border-[#1DB954]/50 text-[#1DB954]"
              >
                <LinkIcon className="w-4 h-4" />
                Lier Spotify
              </button>
            )}
          </div>
          
          {/* Compte GeekQuiz lié */}
          <div className="flex items-center justify-between py-3 border-b border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-xl">
                🧠
              </div>
              <div>
                <p className="font-medium">Question pour un Geek</p>
                <p className="text-dark-400 text-sm">
                  {user?.email
                    ? 'Compte lié par email — quiz de culture geek'
                    : 'Quiz de culture geek sur le même serveur'}
                </p>
              </div>
            </div>
            <a
              href="https://questionpourungeek.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/40 text-emerald-400 text-sm"
            >
              Visiter
            </a>
          </div>

          {/* Déconnexion */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <LogOut className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="font-medium">Déconnexion</p>
                <p className="text-dark-400 text-sm">Se déconnecter de votre compte</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="btn-ghost text-red-400 hover:text-red-300 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </motion.div>
      
      {/* Modal de liaison Spotify */}
      <SpotifyLinkingModal 
        isOpen={showSpotifyModal} 
        onRedirect={handleSpotifyRedirect} 
      />
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  icon,
  subValue,
  small = false,
}: { 
  label: string; 
  value: string | number; 
  icon: React.ReactNode;
  subValue?: string;
  small?: boolean;
}) {
  return (
    <div className={`card text-center ${small ? 'py-3' : ''}`}>
      <div className={`${small ? 'w-8 h-8' : 'w-10 h-10'} mx-auto mb-2 rounded-lg bg-primary-500/20 flex items-center justify-center text-primary-400`}>
        {icon}
      </div>
      <p className={`font-display font-bold ${small ? 'text-xl' : 'text-2xl'}`}>
        {value}
        {subValue && <span className="text-dark-400 text-sm ml-1">({subValue})</span>}
      </p>
      <p className={`text-dark-400 ${small ? 'text-xs' : 'text-sm'}`}>{label}</p>
    </div>
  );
}
